import { DEFAULT_TIMEZONE } from "../../../config/global";
import { BaseAction } from "../../../core/actions/BaseAction";
import { getTranslatedMessage } from "../../../Lib/Utils/i18n";
import stopManager from "../../../Lib/Utils/StopManager";
import { TripTypeEnum } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";
import { ITripSummary } from "../../../Orchestrator/Types/Index";
import { IAddToCalendarActionParams } from "../../../Orchestrator/Types/Interface/ActionParams/AddToCalendarActionParams";
import dayjs from "dayjs";

interface IGenerateIcsFileParams {
  id: string;
  title: string;
  description: string;
  startTime: string;
  endTime: string;
}

export class AddToCalendarAction extends BaseAction<boolean, IAddToCalendarActionParams> {
  override async run(): Promise<boolean> {

    const { trip } = this.params!

    let finalTrips: ITripSummary[] = [];

    let validTripSummaries = [...(trip?.tripSummaries || [])]?.filter(summary => trip.tripIds?.includes(summary.id))
    if (validTripSummaries && validTripSummaries.length > 0) {
      if (trip.bookingType === "oneWayTrip") {
        // by default consider trip direction as outbound it it's not specified.
        const tripDirection = (trip?.direction || TripTypeEnum.OUT_BOUND)?.replace("_", "")
        // replacing _ with empty string to match the direction format OUT_BOUND === OUTBOUND
        finalTrips = validTripSummaries.filter(summary => (summary?.direction?.replace("_", "") === tripDirection));
      } else if (trip.bookingType === "roundTrip") {
        finalTrips = validTripSummaries;
      }
    }

    let calendarEvents: IGenerateIcsFileParams[] = finalTrips.map((tripSummary: ITripSummary) => {
      let from = stopManager.getStopNameById(tripSummary.origin.stopPlaceRef);
      let to = stopManager.getStopNameById(tripSummary.destination.stopPlaceRef);

      return {
        id: tripSummary.id,
        title: `${from} to ${to}`,
        description: `${getTranslatedMessage('bookingRef/PNR')}: ${trip.trainNumber}`,
        startTime: tripSummary.startTime,
        endTime: tripSummary.endTime,
      }
    })

    const generateIcsFile = (events: IGenerateIcsFileParams[]): string => {
      const formatDate = (date: string) => dayjs(date).tz(DEFAULT_TIMEZONE).format("YYYYMMDDTHHmmss");

      // Start the calendar
      let icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Etihad Rail//Booking Calendar//EN
CALSCALE:GREGORIAN
METHOD:PUBLISH`;

      // Add each event
      events.forEach((event, index) => {
        icsContent += `
BEGIN:VEVENT
UID:${event.id}
SUMMARY:${event.title}
DESCRIPTION:${event.description}
DTSTART;TZID=${DEFAULT_TIMEZONE}:${formatDate(event.startTime)}
DTEND;TZID=${DEFAULT_TIMEZONE}:${formatDate(event.endTime)}
DTSTAMP:${dayjs().utc().format("YYYYMMDDTHHmmss")}Z
STATUS:CONFIRMED
TRANSP:OPAQUE
END:VEVENT`;
      });

      // End the calendar
      icsContent += `
END:VCALENDAR`;

      return icsContent.trim();
    };

    const downloadIcsFile = (icsContent: string, filename: string) => {
      const blob = new Blob([icsContent], { type: "text/calendar" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    };

    try {
      const icsFile = generateIcsFile(calendarEvents);

      const fileName = `Etihad-Rail-booking-${trip.trainNumber}.ics`;
      await downloadIcsFile(icsFile, fileName);
    } catch (error) {
      console.error("Error in AddToCalendarAction:", error);
    } finally {
      return true;
    }
  }
}

// Create a singleton instance
export const addToCalendarAction = new AddToCalendarAction(); 